import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { FiPlus, FiEdit2, FiX, FiSave, FiAlertCircle, FiMapPin } from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { collection, addDoc, updateDoc, doc, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getLocationsCached } from '../../utils/masterDataStore';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import usePagePermissions from '../../hooks/usePagePermissions';
import useEscapeClose from '../../hooks/useEscapeClose';
import { clearStockLocationCache } from '../../utils/stockFlowHelper';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  gap: 16px;
  flex-wrap: wrap;
`;

const HeaderText = styled.div``;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0 0 8px 0;
`;

const PageDescription = styled.p`
  font-size: 14px;
  color: ${colors.text.secondary};
  margin: 0;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: ${colors.primary.main};
  color: white;
  border: none;
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${colors.primary.dark};
    transform: translateY(-1px);
  }
`;

const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;

const Th = styled.th`
  padding: 14px 16px;
  text-align: left;
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[200]};
  transition: background 0.2s;

  &:hover {
    background: ${colors.neutral[50]};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const Td = styled.td`
  padding: 16px;
  font-size: 14px;
  color: ${colors.text.primary};
  vertical-align: middle;
`;

const BranchCode = styled.span`
  font-family: 'SF Mono', 'Consolas', monospace;
  font-weight: 600;
  color: ${colors.primary.main};
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  border-radius: ${colors.borderRadius.sm};
  font-size: 12px;
  font-weight: 500;
  background: ${props => props.$active ? colors.success.main + '20' : colors.neutral[200]};
  color: ${props => props.$active ? colors.success.main : colors.text.secondary};
`;

const DefaultBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: ${colors.borderRadius.sm};
  font-size: 10px;
  font-weight: 600;
  background: ${colors.warning.main}20;
  color: ${colors.warning.main};
  text-transform: uppercase;
  margin-left: 8px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
`;

const IconButton = styled.button`
  padding: 8px;
  background: ${props => props.$danger ? colors.error.main + '15' : colors.neutral[100]};
  color: ${props => props.$danger ? colors.error.main : colors.text.secondary};
  border: none;
  border-radius: ${colors.borderRadius.sm};
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${props => props.$danger ? colors.error.main + '25' : colors.neutral[200]};
    color: ${props => props.$danger ? colors.error.main : colors.text.primary};
  }
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: ${colors.borderRadius.sm};
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid;

  ${props => props.$primary && `
    background: ${colors.primary.main};
    border-color: ${colors.primary.main};
    color: white;

    &:hover {
      background: ${colors.primary.dark};
      border-color: ${colors.primary.dark};
    }
  `}

  ${props => props.$warning && `
    background: ${colors.warning.main}20;
    border-color: ${colors.warning.main};
    color: ${colors.warning.main};

    &:hover {
      background: ${colors.warning.main}30;
    }
  `}

  ${props => props.$danger && `
    background: ${colors.error.main}20;
    border-color: ${colors.error.main};
    color: ${colors.error.main};

    &:hover {
      background: ${colors.error.main}30;
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const BranchInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const BranchName = styled.span`
  font-weight: 500;
  color: ${colors.text.primary};
`;

const BranchDetail = styled.span`
  font-size: 12px;
  color: ${colors.text.secondary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  color: ${colors.neutral[300]};
`;

const EmptyText = styled.p`
  font-size: 16px;
  margin: 0 0 8px 0;
`;

const EmptySubtext = styled.p`
  font-size: 14px;
  margin: 0;
`;

const Alert = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  margin-bottom: 20px;
  background: ${props => props.$type === 'success' ? colors.success.main + '15' : colors.error.main + '15'};
  color: ${props => props.$type === 'success' ? colors.success.main : colors.error.main};
  border: 1px solid ${props => props.$type === 'success' ? colors.success.main + '30' : colors.error.main + '30'};
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: ${colors.shadows.lg};
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid ${colors.neutral[200]};
`;

const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 20px;
  color: ${colors.text.secondary};
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${colors.borderRadius.sm};
  transition: all 0.2s;

  &:hover {
    background: ${colors.neutral[100]};
    color: ${colors.text.primary};
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid ${colors.neutral[200]};
  background: ${colors.neutral[50]};
`;

const FormGroup = styled.div`
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: ${colors.text.primary};
  margin-bottom: 6px;

  span {
    color: ${colors.error.main};
    margin-left: 2px;
  }
`;

const HelpText = styled.p`
  margin: 6px 0 0 0;
  font-size: 12px;
  color: ${colors.text.secondary};
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  color: ${colors.text.primary};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }

  &:disabled {
    background: ${colors.neutral[100]};
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background: white;
  color: ${colors.text.primary};
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${colors.neutral[50]};
  }
`;

const SaveButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: ${colors.primary.main};
  color: white;
  border: none;
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: ${colors.primary.dark};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const BranchSetup = () => {
  const { activeCompany, currentUser, companyUser, refreshBranches } = useAuth();
  const { canEdit } = usePagePermissions('settings', 'branchSetup');
  const [branches, setBranches] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  useEscapeClose(showModal, () => setShowModal(false));
  const [editingBranch, setEditingBranch] = useState(null);
  const [alert, setAlert] = useState({ show: false, type: '', message: '' });
  const [formData, setFormData] = useState({
    branchCode: '',
    branchName: '',
    address: '',
    city: '',
    phone: '',
    email: '',
    defaultLocationId: '',
    isDefault: false,
    isActive: true
  });

  const isAdmin = companyUser?.role === 'admin';

  const loadBranches = useCallback(async () => {
    if (!activeCompany?.id) return;

    try {
      setLoading(true);
      const branchesRef = collection(db, 'companies', activeCompany.id, 'branches');
      const q = query(branchesRef, orderBy('branchName'));
      const snapshot = await getDocs(q);

      const branchList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setBranches(branchList);
    } catch (error) {
      console.error('Error loading branches:', error);
      setAlert({ show: true, type: 'error', message: 'Failed to load branches' });
    } finally {
      setLoading(false);
    }
  }, [activeCompany]);

  const loadLocations = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const locRows = await getLocationsCached(activeCompany.id);
      setLocations(locRows.filter(l => l.isActive !== false));
    } catch (e) {
      console.error('Error loading locations:', e);
    }
  }, [activeCompany]);

  useEffect(() => {
    loadBranches();
    loadLocations();
  }, [loadBranches, loadLocations]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const openAddModal = () => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    setEditingBranch(null);
    setFormData({
      branchCode: '',
      branchName: '',
      address: '',
      city: '',
      phone: '',
      email: '',
      defaultLocationId: '',
      isDefault: branches.length === 0,
      isActive: true
    });
    setShowModal(true);
  };

  const openEditModal = (branch) => {
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    setEditingBranch(branch);
    setFormData({
      branchCode: branch.branchCode || '',
      branchName: branch.branchName || '',
      address: branch.address || '',
      city: branch.city || '',
      phone: branch.phone || '',
      email: branch.email || '',
      defaultLocationId: branch.defaultLocationId || '',
      isDefault: branch.isDefault || false,
      isActive: branch.isActive !== false
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    clearStockLocationCache(); // speed-cache safety: refresh remembered settings
    e.preventDefault();
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }

    if (!formData.branchCode || !formData.branchName) {
      setAlert({ show: true, type: 'error', message: 'Please fill in all required fields' });
      return;
    }

    // Check for duplicate branch code
    const existingBranch = branches.find(
      b => b.branchCode.toLowerCase() === formData.branchCode.toLowerCase() &&
        b.id !== editingBranch?.id
    );
    if (existingBranch) {
      setAlert({ show: true, type: 'error', message: 'A branch with this code already exists' });
      return;
    }

    // Package limit guard (creation only). Editing existing branches is always allowed.
    const maxBranches = Number(activeCompany?.maxBranches) || 0;
    if (!editingBranch && maxBranches > 0 && branches.length >= maxBranches) {
      setAlert({
        show: true,
        type: 'error',
        message: `Your package allows up to ${maxBranches} branch${maxBranches === 1 ? '' : 'es'}. Contact your administrator to upgrade.`
      });
      return;
    }

    setSaving(true);
    try {
      const branchesRef = collection(db, 'companies', activeCompany.id, 'branches');
      // Resolve the picked location's name so POS/reports can show it without a lookup.
      const pickedLoc = locations.find(l => l.id === formData.defaultLocationId) || null;
      const locationFields = {
        defaultLocationId: formData.defaultLocationId || null,
        defaultLocationName: pickedLoc?.locationName || null
      };

      // If this branch is being set as default, remove default from others
      if (formData.isDefault) {
        const currentDefault = branches.find(b => b.isDefault && b.id !== editingBranch?.id);
        if (currentDefault) {
          await updateDoc(doc(db, 'companies', activeCompany.id, 'branches', currentDefault.id), {
            isDefault: false,
            updatedAt: new Date().toISOString(),
            updatedBy: currentUser?.uid
          });
        }
      }

      if (editingBranch) {
        // Update existing branch
        await updateDoc(doc(db, 'companies', activeCompany.id, 'branches', editingBranch.id), {
          branchCode: formData.branchCode,
          branchName: formData.branchName,
          address: formData.address,
          city: formData.city,
          phone: formData.phone,
          email: formData.email,
          ...locationFields,
          isDefault: formData.isDefault,
          isActive: formData.isActive,
          updatedAt: new Date().toISOString(),
          updatedBy: currentUser?.uid
        });
        setAlert({ show: true, type: 'success', message: 'Branch updated successfully' });
      } else {
        // Create new branch
        await addDoc(branchesRef, {
          branchCode: formData.branchCode,
          branchName: formData.branchName,
          address: formData.address,
          city: formData.city,
          phone: formData.phone,
          email: formData.email,
          ...locationFields,
          isDefault: formData.isDefault,
          isActive: formData.isActive,
          createdAt: new Date().toISOString(),
          createdBy: currentUser?.uid,
          updatedAt: new Date().toISOString(),
          updatedBy: currentUser?.uid
        });
        setAlert({ show: true, type: 'success', message: 'Branch created successfully' });
      }

      setShowModal(false);
      loadBranches();
      // Push the change into the live session too — otherwise the branch record
      // held since login stays stale and POS keeps using the old stock location.
      refreshBranches?.().catch(() => {});
    } catch (error) {
      console.error('Error saving branch:', error);
      setAlert({ show: true, type: 'error', message: 'Failed to save branch' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (branch) => {
    clearStockLocationCache(); // speed-cache safety: refresh remembered settings
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (!isAdmin) return;

    // Prevent deactivating the default branch
    if (branch.isDefault && branch.isActive) {
      setAlert({ show: true, type: 'error', message: 'Cannot deactivate the default branch. Set another branch as default first.' });
      return;
    }

    try {
      await updateDoc(doc(db, 'companies', activeCompany.id, 'branches', branch.id), {
        isActive: !branch.isActive,
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser?.uid
      });

      setAlert({
        show: true,
        type: 'success',
        message: `Branch ${branch.isActive ? 'deactivated' : 'activated'} successfully`
      });
      loadBranches();
    } catch (error) {
      console.error('Error toggling branch status:', error);
      setAlert({ show: true, type: 'error', message: 'Failed to update branch status' });
    }
  };

  const handleSetDefault = async (branch) => {
    clearStockLocationCache(); // speed-cache safety: refresh remembered settings
    if (!canEdit) { alert('You do not have permission to save changes.'); return; }
    if (!isAdmin) return;

    if (!branch.isActive) {
      setAlert({ show: true, type: 'error', message: 'Cannot set an inactive branch as default' });
      return;
    }

    try {
      // Remove default from current default branch
      const currentDefault = branches.find(b => b.isDefault);
      if (currentDefault) {
        await updateDoc(doc(db, 'companies', activeCompany.id, 'branches', currentDefault.id), {
          isDefault: false,
          updatedAt: new Date().toISOString(),
          updatedBy: currentUser?.uid
        });
      }

      // Set new default
      await updateDoc(doc(db, 'companies', activeCompany.id, 'branches', branch.id), {
        isDefault: true,
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser?.uid
      });

      setAlert({ show: true, type: 'success', message: `"${branch.branchName}" is now the default branch` });
      loadBranches();
    } catch (error) {
      console.error('Error setting default branch:', error);
      setAlert({ show: true, type: 'error', message: 'Failed to set default branch' });
    }
  };

  return (
    <PageLayout pageTitle="Branch Setup" breadcrumb="Settings > Branch Setup">
      <Container>
        <PageHeader>
          <HeaderText>
            <PageTitle>Branch Setup</PageTitle>
            <PageDescription>
              Manage your company branches and locations. One branch must be set as the default.
            </PageDescription>
          </HeaderText>
          {isAdmin && canEdit && (
            <AddButton onClick={openAddModal}>
              <FiPlus />
              Add Branch
            </AddButton>
          )}
        </PageHeader>

        {alert.show && (
          <Alert $type={alert.type}>
            <FiAlertCircle />
            {alert.message}
          </Alert>
        )}

        <ContentCard>
          {loading ? (
            <EmptyState>
              <EmptyText>Loading branches...</EmptyText>
            </EmptyState>
          ) : branches.length === 0 ? (
            <EmptyState>
              <EmptyIcon><FiMapPin /></EmptyIcon>
              <EmptyText>No Branches Created</EmptyText>
              <EmptySubtext>Create your first branch to get started.</EmptySubtext>
            </EmptyState>
          ) : (
            <TableContainer>
              <Table>
                <Thead>
                  <tr>
                    <Th>Branch Code</Th>
                    <Th>Branch Name</Th>
                    <Th>Stock Location</Th>
                    <Th>City</Th>
                    <Th>Contact</Th>
                    <Th>Status</Th>
                    {isAdmin && canEdit && <Th>Actions</Th>}
                  </tr>
                </Thead>
                <Tbody>
                  {branches.map(branch => (
                    <Tr key={branch.id}>
                      <Td>
                        <BranchCode>{branch.branchCode}</BranchCode>
                      </Td>
                      <Td>
                        <BranchInfo>
                          <BranchName>
                            {branch.branchName}
                            {branch.isDefault && <DefaultBadge>Default</DefaultBadge>}
                          </BranchName>
                          {branch.address && (
                            <BranchDetail>{branch.address}</BranchDetail>
                          )}
                        </BranchInfo>
                      </Td>
                      <Td>
                        {branch.defaultLocationName
                          ? branch.defaultLocationName
                          : <BranchDetail style={{ color: colors.warning.main }}>Not set (uses default)</BranchDetail>}
                      </Td>
                      <Td>{branch.city || '-'}</Td>
                      <Td>
                        <BranchInfo>
                          {branch.phone && <BranchDetail>{branch.phone}</BranchDetail>}
                          {branch.email && <BranchDetail>{branch.email}</BranchDetail>}
                          {!branch.phone && !branch.email && '-'}
                        </BranchInfo>
                      </Td>
                      <Td>
                        <Badge $active={branch.isActive}>
                          {branch.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </Td>
                      {isAdmin && canEdit && (
                        <Td>
                          <ActionButtons>
                            <IconButton
                              onClick={() => openEditModal(branch)}
                              title="Edit branch"
                            >
                              <FiEdit2 size={14} />
                            </IconButton>
                            {!branch.isDefault && branch.isActive && (
                              <ActionButton
                                $primary
                                onClick={() => handleSetDefault(branch)}
                                title="Set as default branch"
                              >
                                Set Default
                              </ActionButton>
                            )}
                            <ActionButton
                              $warning={branch.isActive}
                              $primary={!branch.isActive}
                              onClick={() => handleToggleActive(branch)}
                              title={branch.isActive ? 'Deactivate branch' : 'Activate branch'}
                            >
                              {branch.isActive ? 'Deactivate' : 'Activate'}
                            </ActionButton>
                          </ActionButtons>
                        </Td>
                      )}
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          )}
        </ContentCard>

        {/* Branch Modal */}
        {showModal && (
          <ModalOverlay>
            <ModalContent onClick={e => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>{editingBranch ? 'Edit Branch' : 'Add Branch'}</ModalTitle>
                <CloseButton onClick={() => setShowModal(false)}>
                  <FiX />
                </CloseButton>
              </ModalHeader>
              <form onSubmit={handleSubmit}>
                <ModalBody>
                  <FormRow>
                    <FormGroup>
                      <Label>Branch Code <span>*</span></Label>
                      <Input
                        type="text"
                        name="branchCode"
                        value={formData.branchCode}
                        onChange={handleChange}
                        placeholder="e.g., BR-001"
                        required
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Branch Name <span>*</span></Label>
                      <Input
                        type="text"
                        name="branchName"
                        value={formData.branchName}
                        onChange={handleChange}
                        placeholder="e.g., Head Office"
                        required
                      />
                    </FormGroup>
                  </FormRow>

                  <FormGroup>
                    <Label>Address</Label>
                    <Input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      placeholder="Street address"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label>City</Label>
                    <Input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      placeholder="City name"
                    />
                  </FormGroup>

                  <FormRow>
                    <FormGroup>
                      <Label>Phone</Label>
                      <Input
                        type="text"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="Phone number"
                      />
                    </FormGroup>
                    <FormGroup>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Email address"
                      />
                    </FormGroup>
                  </FormRow>

                  <FormGroup>
                    <Label>Stock Location</Label>
                    <Select
                      name="defaultLocationId"
                      value={formData.defaultLocationId}
                      onChange={handleChange}
                    >
                      <option value="">— Use company default —</option>
                      {locations.map(loc => (
                        <option key={loc.id} value={loc.id}>{loc.locationName}</option>
                      ))}
                    </Select>
                    <HelpText>
                      Sales at this branch (POS &amp; invoices) remove stock from this location.
                      {locations.length === 0 && ' Create locations under Master Data → Inventory Locations first.'}
                    </HelpText>
                  </FormGroup>

                  <FormRow>
                    <FormGroup>
                      <Label>Status</Label>
                      <Select
                        name="isActive"
                        value={formData.isActive ? 'true' : 'false'}
                        onChange={(e) => setFormData(prev => ({ ...prev, isActive: e.target.value === 'true' }))}
                      >
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </Select>
                    </FormGroup>
                    <FormGroup>
                      <Label>Default Branch</Label>
                      <Select
                        name="isDefault"
                        value={formData.isDefault ? 'true' : 'false'}
                        onChange={(e) => setFormData(prev => ({ ...prev, isDefault: e.target.value === 'true' }))}
                      >
                        <option value="false">No</option>
                        <option value="true">Yes</option>
                      </Select>
                    </FormGroup>
                  </FormRow>
                </ModalBody>
                <ModalFooter>
                  <CancelButton type="button" onClick={() => setShowModal(false)}>
                    Cancel
                  </CancelButton>
                  {canEdit && (
                    <SaveButton type="submit" disabled={saving}>
                      <FiSave />
                      {saving ? 'Saving...' : editingBranch ? 'Update Branch' : 'Create Branch'}
                    </SaveButton>
                  )}
                </ModalFooter>
              </form>
            </ModalContent>
          </ModalOverlay>
        )}
      </Container>
    </PageLayout>
  );
};

export default BranchSetup;
